import { action, observable } from 'mobx';
import * as Models from 'Models/Entities';
import {
	attribute,
	entity,
	IModelAttributes,
	Model,
	ReferencePath,
} from 'Models/Model';
import UserAddressEntity from 'Models/Security/Acl/UserAddressEntity';
import VisitorsAddressEntity from 'Models/Security/Acl/VisitorsAddressEntity';
import { IAcl } from 'Models/Security/IAcl';
import * as AttrUtils from 'Util/AttributeUtils';
import {
	getCreatedModifiedCrudOptions,
	makeFetchOneToManyFunc,
} from 'Util/EntityUtils';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { CRUD } from '../CRUDOptions';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';

export interface IAddress {
	line1: string;
	city: string;
	state: string;
	line2: string;
	suburb: string;
	postcode: string;
	country: string;
}

export interface IAddressTyped extends IAddress {
	type: string;
}

export const AddressModifiableFields = [
	'line1', 'line2', 'suburb', 'state', 'postcode', 'country'
] as (keyof AddressEntity)[];

export interface IAddressEntityAttributes extends IAddress, IModelAttributes {
	atbRecordId?: string;
	atbRecord?: Models.AtbRecordEntity | Models.IAtbRecordEntityAttributes;
	businessEntityId?: string;
	businessEntity?: Models.BusinessEntity | Models.IBusinessEntityAttributes;
	customerWithPhysicalAddressId?: string;
	customerWithPhysicalAddress?: Models.CustomerEntity | Models.ICustomerEntityAttributes;
	customerWithPostalAddressId?: string;
	customerWithPostalAddress?: Models.CustomerEntity | Models.ICustomerEntityAttributes;
}

@entity('AddressEntity', 'Address')
export default class AddressEntity extends Model
	implements IAddressEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsAddressEntity(),
		new UserAddressEntity(),
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * Line 1
	 */
	@observable
	@attribute<AddressEntity, string>()
	@CRUD({
		name: 'Line 1',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public line1: string;

	@observable
	@attribute<AddressEntity, string>()
	@CRUD({
		name: 'City',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public city: string;

	/**
	 * State
	 */
	@observable
	@attribute<AddressEntity, string>()
	@CRUD({
		name: 'State',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public state: string;

	/**
	 * Line 2
	 */
	@observable
	@attribute<AddressEntity, string>()
	@CRUD({
		name: 'Line 2',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public line2: string;

	/**
	 * Suburb
	 */
	@observable
	@attribute<AddressEntity, string>()
	@CRUD({
		name: 'Suburb',
		displayType: 'textfield',
		order: 50,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public suburb: string;

	/**
	 * Postcode
	 */
	@observable
	@attribute<AddressEntity, string>()
	@CRUD({
		name: 'Postcode',
		displayType: 'textfield',
		order: 60,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public postcode: string;

	/**
	 * Country
	 */
	@observable
	@attribute<AddressEntity, string>()
	@CRUD({
		name: 'Country',
		displayType: 'textfield',
		order: 70,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public country: string;

	@observable
	@attribute()
	@CRUD({
		name: 'ATB Record',
		displayType: 'reference-combobox',
		order: 80,
		referenceTypeFunc: () => Models.AtbRecordEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'atbRecords',
			oppositeEntity: () => Models.AtbRecordEntity,
		}),
	})
	public atbRecordId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public atbRecord: Models.AtbRecordEntity;

	/**
	 * Address
	 */
	@observable
	@attribute()
	@CRUD({
		name: 'Business Entity',
		displayType: 'reference-combobox',
		order: 90,
		referenceTypeFunc: () => Models.BusinessEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'businessEntitys',
			oppositeEntity: () => Models.BusinessEntity,
		}),
	})
	public businessEntityId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public businessEntity: Models.BusinessEntity;

	/**
	 * Physical Address
	 */
	@observable
	@attribute()
	@CRUD({
		name: 'Customer with Physical Address',
		displayType: 'reference-combobox',
		order: 100,
		referenceTypeFunc: () => Models.CustomerEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'customerWithPhysicalAddresss',
			oppositeEntity: () => Models.CustomerEntity,
		}),
	})
	public customerWithPhysicalAddressId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public customerWithPhysicalAddress: Models.CustomerEntity;

	/**
	 * Postal Address
	 */
	@observable
	@attribute()
	@CRUD({
		name: 'Customer with Postal Address',
		displayType: 'reference-combobox',
		order: 110,
		referenceTypeFunc: () => Models.CustomerEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'customerWithPostalAddresss',
			oppositeEntity: () => Models.CustomerEntity,
		}),
	})
	public customerWithPostalAddressId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public customerWithPostalAddress: Models.CustomerEntity;

	@action public validateField = async (field: string) => {
		switch (field) {
			case 'postcode': {
				this.setFieldError(
					'postcode',
					!!this.postcode && !this.postcode.match(/^[0-9]{4}$/),
					'Postcode must comprise of four numbers',
				);
				break;
			}
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IAddressEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IAddressEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.line1 !== undefined) {
				this.line1 = attributes.line1;
			}
			if (attributes.city !== undefined) {
				this.city = attributes.city;
			}
			if (attributes.state !== undefined) {
				this.state = attributes.state;
			}
			if (attributes.line2 !== undefined) {
				this.line2 = attributes.line2;
			}
			if (attributes.suburb !== undefined) {
				this.suburb = attributes.suburb;
			}
			if (attributes.postcode !== undefined) {
				this.postcode = attributes.postcode;
			}
			if (attributes.country !== undefined) {
				this.country = attributes.country;
			}
			if (attributes.atbRecordId !== undefined) {
				this.atbRecordId = attributes.atbRecordId;
			}
			if (attributes.atbRecord !== undefined) {
				if (attributes.atbRecord === null) {
					this.atbRecord = attributes.atbRecord;
				} else if (attributes.atbRecord instanceof Models.AtbRecordEntity) {
					this.atbRecord = attributes.atbRecord;
					this.atbRecordId = attributes.atbRecord.id;
				} else {
					this.atbRecord = new Models.AtbRecordEntity(attributes.atbRecord);
					this.atbRecordId = this.atbRecord.id;
				}
			}
			if (attributes.businessEntityId !== undefined) {
				this.businessEntityId = attributes.businessEntityId;
			}
			if (attributes.businessEntity !== undefined) {
				if (attributes.businessEntity === null) {
					this.businessEntity = attributes.businessEntity;
				} else if (attributes.businessEntity instanceof Models.BusinessEntity) {
					this.businessEntity = attributes.businessEntity;
					this.businessEntityId = attributes.businessEntity.id;
				} else {
					this.businessEntity = new Models.BusinessEntity(attributes.businessEntity);
					this.businessEntityId = this.businessEntity.id;
				}
			}
			if (attributes.customerWithPhysicalAddressId !== undefined) {
				this.customerWithPhysicalAddressId = attributes.customerWithPhysicalAddressId;
			}
			if (attributes.customerWithPhysicalAddress !== undefined) {
				if (attributes.customerWithPhysicalAddress === null) {
					this.customerWithPhysicalAddress = attributes.customerWithPhysicalAddress;
				} else if (attributes.customerWithPhysicalAddress instanceof Models.CustomerEntity) {
					this.customerWithPhysicalAddress = attributes.customerWithPhysicalAddress;
					this.customerWithPhysicalAddressId = attributes.customerWithPhysicalAddress.id;
				} else {
					this.customerWithPhysicalAddress = new Models.CustomerEntity(attributes.customerWithPhysicalAddress);
					this.customerWithPhysicalAddressId = this.customerWithPhysicalAddress.id;
				}
			}
			if (attributes.customerWithPostalAddressId !== undefined) {
				this.customerWithPostalAddressId = attributes.customerWithPostalAddressId;
			}
			if (attributes.customerWithPostalAddress !== undefined) {
				if (attributes.customerWithPostalAddress === null) {
					this.customerWithPostalAddress = attributes.customerWithPostalAddress;
				} else if (attributes.customerWithPostalAddress instanceof Models.CustomerEntity) {
					this.customerWithPostalAddress = attributes.customerWithPostalAddress;
					this.customerWithPostalAddressId = attributes.customerWithPostalAddress.id;
				} else {
					this.customerWithPostalAddress = new Models.CustomerEntity(attributes.customerWithPostalAddress);
					this.customerWithPostalAddressId = this.customerWithPostalAddress.id;
				}
			}
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		atbRecord {
			${Models.AtbRecordEntity.getAttributes().join('\n')}
		}
		businessEntity {
			${Models.BusinessEntity.getAttributes().join('\n')}
		}
		customerWithPhysicalAddress {
			${Models.CustomerEntity.getAttributes().join('\n')}
		}
		customerWithPostalAddress {
			${Models.CustomerEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'atbRecord',
							'businessEntity',
							'customerWithPhysicalAddress',
							'customerWithPostalAddress',
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		const localAddress = (this.line1 ? this.line1 : '')
			+ (this.line2 ? ` ${this.line2}` : '');

		return (localAddress ? `${localAddress},` : '')
			+ (this.suburb ? ` ${this.suburb}` : '')
			+ (this.state ? ` ${this.state}` : '')
			+ (this.postcode ? ` ${this.postcode}` : '');
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(AddressEntity.prototype, 'created');
CRUD(modifiedAttr)(AddressEntity.prototype, 'modified');
